import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { 
  ArrowLeft, 
  BookOpen, 
  Calculator, 
  FlaskConical, 
  Users,
  Send,
  Bot,
  User,
  Loader2,
  Sparkles,
  Star,
  Flame,
  ChevronRight,
  PenTool,
  ArrowRight,
  Clock,
  Coffee,
  Brain,
  CheckCircle2,
  Circle,
  Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { BloomThermometer, BloomBadge } from "@/components/BloomThermometer";
import MathRenderer from "@/components/MathRenderer";
import MathWhiteboard from "@/components/MathWhiteboard";
import ScienceWhiteboard from "@/components/ScienceWhiteboard";
import IndicatorProgressPanel from "@/components/IndicatorProgressPanel";
import ChatInput from "@/components/chat/ChatInput";
import MessageBubble from "@/components/chat/MessageBubble";

const subjectConfig = {
  "comunicacion-lenguaje": {
    name: "Comunicación y Lenguaje",
    icon: BookOpen,
    color: "#ef4444",
    bgColor: "bg-red-50",
    hasMathSupport: false
  },
  "ciencia-tecnologia": {
    name: "Ciencia y Tecnología",
    icon: FlaskConical,
    color: "#14b8a6",
    bgColor: "bg-teal-50",
    hasMathSupport: true,
    scienceMode: true
  },
  "matematica-datos": {
    name: "Matemática y Datos",
    icon: Calculator,
    color: "#8b5cf6",
    bgColor: "bg-purple-50",
    hasMathSupport: true
  },
  "ciudadania-valores": {
    name: "Ciudadanía y Valores",
    icon: Users,
    color: "#f59e0b",
    bgColor: "bg-amber-50",
    hasMathSupport: false
  },
  "reto-avanzo": {
    name: "Reto Avanzo",
    icon: Brain,
    color: "#0ea5e9",
    bgColor: "bg-sky-50",
    hasMathSupport: false,
    retoAvanzo: true
  }
};

// Detectar nivel de Bloom de la respuesta del tutor
const detectBloomLevel = (content) => {
  const contentLower = content.toLowerCase();
  
  if (contentLower.includes("crea") || contentLower.includes("escribe") || contentLower.includes("produce") || contentLower.includes("diseña") || contentLower.includes("inventa")) {
    return "crear";
  }
  if (contentLower.includes("evalúa") || contentLower.includes("opina") || contentLower.includes("juzga") || contentLower.includes("¿qué opinas") || contentLower.includes("¿es correcto")) {
    return "evaluar";
  }
  if (contentLower.includes("analiza") || contentLower.includes("compara") || contentLower.includes("diferencia") || contentLower.includes("¿por qué crees") || contentLower.includes("¿qué diferencia")) {
    return "analizar";
  }
  if (contentLower.includes("aplica") || contentLower.includes("usa") || contentLower.includes("resuelve") || contentLower.includes("ejemplo") || contentLower.includes("ejercicio")) {
    return "aplicar";
  }
  if (contentLower.includes("explica") || contentLower.includes("describe") || contentLower.includes("¿qué significa") || contentLower.includes("con tus palabras")) {
    return "comprender";
  }
  
  return "recordar";
};

// ── Daily limit reached screen ────────────────────────────────────────────────
const LimitReachedScreen = ({ navigate }) => {
  const resumeHour = new Date(Date.now() + 3 * 60 * 60 * 1000);
  const resumeTime = resumeHour.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" });

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      className="min-h-screen bg-gradient-to-br from-[#e8f4fc] to-[#f0ebff] flex items-center justify-center p-6"
      data-testid="limit-reached-screen"
    >
      <div className="max-w-md w-full text-center space-y-6">
        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#419cca] to-[#8b5cf6] mx-auto flex items-center justify-center shadow-lg">
          <Coffee className="w-12 h-12 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">¡Excelente sesión de aprendizaje!</h1>
          <p className="text-gray-500 mt-2 text-sm">
            Completaste tus <span className="font-semibold text-[#419cca]">45 minutos</span> de estudio con QHUB.BUDDY por hoy.
          </p>
        </div>
        <div className="bg-white rounded-2xl p-5 border border-[#419cca]/20 shadow-sm text-left space-y-3">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-[#419cca] mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-gray-800 text-sm">Pausa activa recomendada</p>
              <p className="text-gray-500 text-sm">El aprendizaje mejora con descanso. Toma al menos <strong>3 horas</strong> antes de continuar.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Flame className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-gray-800 text-sm">Puedes retomar a las {resumeTime}</p>
              <p className="text-gray-500 text-sm">Camina, toma agua o estira. Tu cerebro seguirá procesando lo que aprendiste.</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <Button onClick={() => navigate("/dashboard")} className="w-full bg-gradient-to-r from-[#419cca] to-[#8b5cf6] text-white h-12 rounded-xl" data-testid="limit-go-dashboard-btn">
            Volver al inicio
          </Button>
          <Button variant="ghost" onClick={() => navigate(-1)} className="w-full text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-4 h-4 mr-2" /> Regresar
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

// Generates a stable unique key for chat messages (avoids index-as-key issues
// that break AnimatePresence reconciliation when messages are appended).
const newMsgId = () => `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const TutorChat = () => {
  const { subject } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [limitReached, setLimitReached] = useState(false);
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionPoints, setSessionPoints] = useState(0);
  const [totalPoints, setTotalPoints] = useState(0);
  const [streak, setStreak] = useState(0);
  const [currentBloomLevel, setCurrentBloomLevel] = useState("recordar");
  const [showSidebar, setShowSidebar] = useState(true);
  const [showWhiteboard, setShowWhiteboard] = useState(false);
  const [unitContents, setUnitContents] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("");
  const [contentsLoading, setContentsLoading] = useState(false);
  const [showTopicSelector, setShowTopicSelector] = useState(true);
  const [recentTopics, setRecentTopics] = useState([]);
  // Incrementar este contador refresca el panel de progreso de indicadores
  // (se hace tras cada respuesta del tutor por si completó algún indicador)
  const [indicatorsRefreshKey, setIndicatorsRefreshKey] = useState(0);

  const COMPLETION_NOTE_TEXT = "Has completado un indicador de esta unidad";

  const parseContentOrder = (text) => {
    if (!text) return null;
    const match = text.trim().match(/^(\d+)[\.|\-|\)]/);
    return match ? Number(match[1]) : null;
  };
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const pasteStateRef = useRef({ pasted: false, paste_chars: 0, typed_chars: 0 });

  const unitCode = location.state?.unitCode || null;
  const unitName = location.state?.unitName || null;
  const preselectedTopic = location.state?.preselectedTopic || null;

  const config = subjectConfig[subject] || subjectConfig["comunicacion-lenguaje"];
  const Icon = config.icon;
  
  const studentName = user?.profile?.first_name || user?.name?.split(" ")[0] || "Estudiante";

  // Fetch initial stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats/me`, { withCredentials: true });
        setTotalPoints(response.data.points || 0);
        setStreak(response.data.streak || 0);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };
    fetchStats();
  }, []);

  // Check daily usage limit on mount
  useEffect(() => {
    const checkDailyLimit = async () => {
      try {
        const res = await axios.get(`${API}/tutor/daily-usage`, { withCredentials: true });
        if (res.data.limit_reached) setLimitReached(true);
      } catch (error) {
        console.error("Error checking daily limit:", error);
      }
    };
    checkDailyLimit();
  }, []);

  // Reto Avanzo: gate entry behind the initial diagnostic
  useEffect(() => {
    if (!config.retoAvanzo || !user) return;
    const checkDiag = async () => {
      try {
        const res = await axios.get(`${API}/reto-avanzo/diagnostic/status`, { withCredentials: true });
        if (!res.data?.completed) {
          navigate("/reto-avanzo/diagnostico");
        }
      } catch (err) {
        // If the API doesn't exist or fails, allow the chat (graceful)
        console.error("Diagnostic check failed:", err);
      }
    };
    checkDiag();
  }, [config.retoAvanzo, user, navigate]);

  useEffect(() => {
    const fetchContents = async () => {
      if (config.retoAvanzo) {
        setUnitContents([]);
        return;
      }
      if (!unitName || !user) return;
      try {
        setContentsLoading(true);
        const gradeValue = user.grade || "8";
        const response = await axios.get(
          `${API}/curriculum/${gradeValue}/${subject}/indicators`,
          {
            params: { unit: unitName },
            withCredentials: true,
          }
        );
        const rows = response.data || [];
        const items = rows
          .map((row, index) => {
            const text = (row.conceptual_content || row.contents || "").trim();
            return {
              text,
              order: typeof row.content_order === "number" ? row.content_order : null,
              prefixOrder: parseContentOrder(text),
              index,
            };
          })
          .filter((item) => item.text && item.text.toLowerCase() !== "nan");

        items.sort((a, b) => {
          if (a.order !== null && b.order !== null && a.order !== b.order) {
            return a.order - b.order;
          }
          if (a.order !== null && b.order === null) return -1;
          if (b.order !== null && a.order === null) return 1;
          if (a.prefixOrder !== null && b.prefixOrder !== null && a.prefixOrder !== b.prefixOrder) {
            return a.prefixOrder - b.prefixOrder;
          }
          if (a.prefixOrder !== null && b.prefixOrder === null) return -1;
          if (b.prefixOrder !== null && a.prefixOrder === null) return 1;
          return a.index - b.index;
        });

        // Deduplicate: strip numeric prefix and keep unique base names
        const seen = new Set();
        const dedupedTopics = [];
        for (const item of items) {
          const baseName = item.text.replace(/^\d+[\.\-\)]\s*/, "").trim();
          const key = baseName.toLowerCase();
          if (!seen.has(key) && baseName) {
            seen.add(key);
            dedupedTopics.push(baseName);
          }
        }

        setUnitContents(dedupedTopics);
        if (preselectedTopic && !selectedTopic) {
          const preselectedClean = preselectedTopic.replace(/^\d+[\.\-\)]\s*/, "").trim().toLowerCase();
          const match = dedupedTopics.find(
            (t) => t.toLowerCase().includes(preselectedClean) || preselectedClean.includes(t.toLowerCase())
          );
          if (match) {
            setSelectedTopic(match);
            setShowTopicSelector(false);
          }
        }
      } catch (error) {
        console.error("Error fetching contents:", error);
        setUnitContents([]);
      } finally {
        setContentsLoading(false);
      }
    };
    fetchContents();
  }, [unitName, subject, user, config.retoAvanzo, preselectedTopic, selectedTopic]);

  const topicRequired = unitName && unitContents.length > 0 && !selectedTopic;

  const handleChangeTopic = useCallback(() => {
    setSelectedTopic("");
    setShowTopicSelector(true);
    const selector = document.querySelector('[data-testid="topic-selector"]');
    if (selector) {
      selector.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  useEffect(() => {
    let greeting;
    if (config.retoAvanzo) {
      greeting = {
        id: newMsgId(),
        role: "assistant",
        content: `Hola ${studentName}. Soy **Reto Avanzo**, tu entrenador cognitivo.\n\nAquí no estudiamos materias — entrenamos cómo piensas: comprensión, análisis, argumentación y resolución de problemas reales.\n\n¿En qué quieres empezar a trabajar hoy? Puedo plantearte un caso, un dilema o un reto interdisciplinario.`,
      };
    } else {
      const baseGreeting = `Hola ${studentName}. Soy **QHUB.BUDDY**, tu tutor de **${config.name}**. ${unitName ? `Vamos a trabajar en la unidad: **${unitName}**.` : ""}`;
      const topicLine = selectedTopic
        ? `Tema seleccionado: **${selectedTopic}**.`
        : unitName && unitContents.length > 0
          ? "Selecciona un tema de la lista para iniciar."
          : "";
      const closingLine = selectedTopic
        ? "¿Listo para comenzar?"
        : "¿Qué tema te gustaría explorar hoy?";
      greeting = {
        id: newMsgId(),
        role: "assistant",
        content: `${baseGreeting}\n\n${topicLine}\n\n${closingLine}`.trim()
      };
    }

    setMessages((prev) => {
      if (prev.length > 1) return prev;
      return [greeting];
    });
  }, [config.name, config.retoAvanzo, unitName, studentName, selectedTopic, unitContents.length]);

  useEffect(() => {
    // Scroll solo el contenedor de mensajes para evitar que la ventana
    // (document) scrolee cuando la pizarra está abierta y temporalmente
    // hace que el layout supere el alto del viewport.
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    if (topicRequired) {
      toast.error("Selecciona un tema para iniciar");
      return;
    }

    const userMessage = { id: newMsgId(), role: "user", content: input.trim() };
    const streamingMsgId = newMsgId();
    const streamingMessage = { id: streamingMsgId, role: "assistant", content: "" };
    const newMessages = [...messages, userMessage];
    setMessages([...newMessages, streamingMessage]);
    setInput("");
    setLoading(true);

    // Snapshot paste meta and reset for next message
    const pasteMeta = { ...pasteStateRef.current };
    pasteStateRef.current = { pasted: false, paste_chars: 0, typed_chars: 0 };

    let accumulated = "";
    let pointsEarned = 0;

    try {
      const res = await fetch(`${API}/tutor/${subject}/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          grade: user.grade || "8",
          subject: subject,
          unit_code: unitCode,
          unit_name: unitName,
          topic: selectedTopic || null,
          pasted: pasteMeta.pasted,
          paste_chars: pasteMeta.paste_chars,
          typed_chars: pasteMeta.typed_chars,
          messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      if (res.status === 429) {
        setLimitReached(true);
        return;
      }
      if (!res.ok || !res.body) {
        const detail = (await res.json().catch(() => ({})))?.detail || "Error al enviar el mensaje";
        throw new Error(detail);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let done = false;
      while (!done) {
        const { value, done: streamDone } = await reader.read();
        done = streamDone;
        if (value) buffer += decoder.decode(value, { stream: true });

        // Parse full SSE events terminated by \n\n
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          if (!raw.startsWith("data:")) continue;
          const payload = raw.slice(5).trim();
          if (!payload) continue;
          let evt;
          try { evt = JSON.parse(payload); } catch { continue; }

          if (evt.type === "token" && evt.text) {
            accumulated += evt.text;
            setMessages((prev) =>
              prev.map((m) => (m.id === streamingMsgId ? { ...m, content: accumulated } : m)),
            );
          } else if (evt.type === "meta") {
            pointsEarned = Number(evt.points_earned) || 0;
          } else if (evt.type === "error") {
            // If backend forwards HTTP status (e.g. 429 daily-limit) we need
            // to trip the LimitReachedScreen instead of a generic toast.
            const err = new Error(evt.message || "Error del tutor");
            err.status = evt.status;
            throw err;
          }
        }
      }

      // Post-stream side-effects (Bloom detect, points toast, indicator refresh)
      setIndicatorsRefreshKey((k) => k + 1);
      const detectedLevel = detectBloomLevel(accumulated);
      setCurrentBloomLevel(detectedLevel);
      if (pointsEarned > 0) {
        setSessionPoints((prev) => prev + pointsEarned);
        setTotalPoints((prev) => prev + pointsEarned);
        toast.success(`+${pointsEarned} puntos`, {
          description: `Nivel: ${detectedLevel.charAt(0).toUpperCase() + detectedLevel.slice(1)}`,
        });
      }
    } catch (error) {
      console.error("Error sending message (stream):", error);
      // Remove the empty streaming bubble on failure
      setMessages(newMessages);
      // Handle daily-limit reached (HTTP 429 either from initial fetch or SSE error event)
      if (error?.status === 429) {
        setLimitReached(true);
        return;
      }
      const msg = error?.message || "Error al enviar el mensaje";
      if (msg.toLowerCase().includes("no está disponible") || msg.toLowerCase().includes("no esta disponible")) {
        toast.error("El tutor no está disponible", { description: "Intenta de nuevo en unos segundos" });
      } else {
        toast.error(msg, { description: "Por favor intenta de nuevo" });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Latest-ref pattern ────────────────────────────────────────────────
  // handleSend, handleKeyDown y handleWhiteboardSubmit dependen de muchos
  // estados (input, messages, loading, ...). Con `useCallback + deps` se
  // recrearían en cada keystroke y romperían la memoización de <ChatInput>.
  // Usamos refs que apuntan a la implementación viva y exponemos wrappers
  // ESTABLES (identidad constante) que delegan a la ref actual.
  const handleSendRef = useRef(handleSend);
  const handleKeyDownRef = useRef(handleKeyDown);
  const handleWhiteboardSubmitRef = useRef(null);
  useEffect(() => { handleSendRef.current = handleSend; });
  useEffect(() => { handleKeyDownRef.current = handleKeyDown; });
  const stableHandleSend = useCallback(() => handleSendRef.current?.(), []);
  const stableHandleKeyDown = useCallback((e) => handleKeyDownRef.current?.(e), []);
  const stableHandleWhiteboardSubmit = useCallback(
    (content, steps) => handleWhiteboardSubmitRef.current?.(content, steps),
    [],
  );

  // Handlers memoizados para <ChatInput /> (evita re-renders innecesarios).
  const handleInputChange = useCallback(
    (e) => {
      const next = e.target.value;
      // Heurística: si la longitud crece de a 1 char, fue tipeo.
      // Si el delta es grande (>15 chars), tratamos como pegado/IME.
      setInput((prev) => {
        const delta = next.length - prev.length;
        if (delta === 1) {
          pasteStateRef.current.typed_chars += 1;
        } else if (delta > 15) {
          pasteStateRef.current.pasted = true;
          pasteStateRef.current.paste_chars += delta;
        }
        if (next.length === 0) {
          pasteStateRef.current = { pasted: false, paste_chars: 0, typed_chars: 0 };
        }
        return next;
      });
    },
    [],
  );

  const handleInputPaste = useCallback((e) => {
    const pasted = (e.clipboardData || window.clipboardData)?.getData("text") || "";
    if (pasted.length > 0) {
      pasteStateRef.current.pasted = true;
      pasteStateRef.current.paste_chars += pasted.length;
    }
  }, []);

  // Handle whiteboard submission
  const handleWhiteboardSubmit = async (content, steps) => {
    if (loading) return;
    if (topicRequired) {
      toast.error("Selecciona un tema para iniciar");
      return;
    }

    const formattedContent = `📝 **Mi desarrollo en la pizarra:**\n\n${content}\n\n¿Puedes verificar si mi procedimiento es correcto?`;
    
    const userMessage = { id: newMsgId(), role: "user", content: formattedContent };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setShowWhiteboard(false);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/tutor/${subject}`,
        {
          grade: user.grade || "8",
          subject: subject,
          unit_code: unitCode,
          unit_name: unitName,
          topic: selectedTopic || null,
          messages: newMessages.map(m => ({
            role: m.role,
            content: m.content
          })),
          whiteboard_content: steps // Send the steps for better analysis
        },
        { withCredentials: true }
      );

      const assistantContent = response.data.response;
      const assistantMessage = {
        id: newMsgId(),
        role: "assistant",
        content: assistantContent
      };

      setMessages([...newMessages, assistantMessage]);

      // Refrescar panel de indicadores (puede haber completado uno)
      setIndicatorsRefreshKey(k => k + 1);

      const detectedLevel = detectBloomLevel(assistantContent);
      setCurrentBloomLevel(detectedLevel);

      if (response.data.points_earned > 0) {
        setSessionPoints(prev => prev + response.data.points_earned);
        setTotalPoints(prev => prev + response.data.points_earned);
        toast.success(`+${response.data.points_earned} puntos`, {
          description: `Nivel: ${detectedLevel.charAt(0).toUpperCase() + detectedLevel.slice(1)}`
        });
      }

    } catch (error) {
      console.error("Error sending whiteboard:", error);
      toast.error("Error al enviar la pizarra", {
        description: "Por favor intenta de nuevo"
      });
    } finally {
      setLoading(false);
    }
  };

  // Attach latest handleWhiteboardSubmit impl to ref (see latest-ref pattern above).
  useEffect(() => { handleWhiteboardSubmitRef.current = handleWhiteboardSubmit; });

  // Show limit-reached screen if daily quota exhausted
  if (limitReached) return <LimitReachedScreen navigate={navigate} />;

  return (
    <div className="h-screen flex bg-[#f8fafc] overflow-hidden" data-testid="tutor-chat-page">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header 
          className="shrink-0 border-b"
          style={{ backgroundColor: `${config.color}10`, borderColor: `${config.color}20` }}
        >
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-600 hover:text-gray-900 hover:bg-white/50"
                onClick={() => navigate(config.retoAvanzo ? "/dashboard" : `/rutas/${subject}`)}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md"
                style={{ backgroundColor: config.color }}
              >
                <Icon className="w-5 h-5 text-white" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h1 className="font-bold text-gray-900 truncate">{config.name}</h1>
                {unitName && (
                  <p className="text-xs text-gray-500 truncate">{unitName}</p>
                )}
              </div>

              {/* Stats badges (mobile) */}
              {!config.retoAvanzo && (
                <div className="flex items-center gap-2 md:hidden">
                  <BloomBadge currentLevel={currentBloomLevel} points={totalPoints} />
                </div>
              )}

              {/* Toggle sidebar button (mobile) */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden text-gray-500"
                onClick={() => setShowSidebar(!showSidebar)}
              >
                <ChevronRight className={`w-5 h-5 transition-transform ${showSidebar ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </div>
        </header>

        {/* Messages */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto min-h-0">
          <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
            {unitName && unitContents.length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm" data-testid="topic-selector">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Elige el tema para iniciar</h3>
                    <p className="text-xs text-gray-500">Selecciona un contenido específico de la unidad.</p>
                  </div>
                  {selectedTopic && (
                    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-blue-50 text-blue-700" data-testid="topic-selected-badge">
                      Tema seleccionado
                    </span>
                  )}
                </div>
                {selectedTopic && !showTopicSelector && (
                  <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 flex items-center justify-between gap-3" data-testid="topic-collapsed">
                    <div>
                      <p className="text-xs text-blue-700">Tema actual</p>
                      <p className="text-sm font-semibold text-blue-800" data-testid="topic-collapsed-text">{selectedTopic}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowTopicSelector(true)}
                      className="text-xs font-semibold text-blue-700 hover:text-blue-800"
                      data-testid="topic-expand-btn"
                    >
                      Cambiar tema
                    </button>
                  </div>
                )}
                {contentsLoading ? (
                  <p className="text-xs text-gray-400" data-testid="topic-loading">Cargando contenidos...</p>
                ) : showTopicSelector ? (
                  <div className="grid sm:grid-cols-2 gap-2" data-testid="topic-options">
                    {unitContents.map((topic, index) => (
                      <button
                        key={`${topic}-${index}`}
                        type="button"
                        onClick={() => {
                          setSelectedTopic(topic);
                          setShowTopicSelector(false);
                          setRecentTopics((prev) => {
                            const next = [topic, ...prev.filter((t) => t !== topic)];
                            return next.slice(0, 3);
                          });
                        }}
                        className={`text-left text-sm px-3 py-2 rounded-xl border transition-all ${
                          selectedTopic === topic
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-gray-200 text-gray-600 hover:border-gray-300"
                        }`}
                        data-testid={`topic-option-${index}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span>{topic}</span>
                          {recentTopics.includes(topic) && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-testid={`topic-recent-${index}`}>
                              Reciente
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : null}
                {topicRequired && (
                  <p className="text-xs text-amber-600 mt-3" data-testid="topic-required-text">Selecciona un tema para habilitar el chat.</p>
                )}
              </div>
            )}

            <AnimatePresence>
              {messages.map((message, index) => (
                <MessageBubble
                  key={message.id || `m-idx-${index}`}
                  message={message}
                  configColor={config.color}
                  hasMathSupport={config.hasMathSupport}
                  onChangeTopic={handleChangeTopic}
                />
              ))}
            </AnimatePresence>

            {loading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-3"
              >
                <div 
                  className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: config.color }}
                >
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="bg-white border border-gray-100 shadow-sm rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">QHUB.BUDDY está pensando...</span>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Input */}
        <ChatInput
          input={input}
          onInputChange={handleInputChange}
          onPaste={handleInputPaste}
          onKeyDown={stableHandleKeyDown}
          onSend={stableHandleSend}
          loading={loading}
          topicRequired={topicRequired}
          hasMathSupport={config.hasMathSupport}
          scienceMode={config.scienceMode}
          configColor={config.color}
          showWhiteboard={showWhiteboard}
          setShowWhiteboard={setShowWhiteboard}
          onWhiteboardSubmit={stableHandleWhiteboardSubmit}
          textareaRef={textareaRef}
        />
      </div>

      {/* Sidebar with Thermometer */}
      <AnimatePresence>
        {showSidebar && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            className="hidden md:block border-l border-gray-100 bg-gray-50/50 overflow-hidden"
          >
            <div className="p-4 space-y-4">
              {config.retoAvanzo ? (
                <>
                  <div className="bg-white rounded-2xl border border-gray-100 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Brain className="w-4 h-4 text-sky-500" />
                      <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Reto Avanzo</p>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Entrenamiento cognitivo adaptativo. Aquí no estudias materias, entrenas cómo piensas.
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl border border-gray-100 p-4">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Qué desarrollas</p>
                    <ul className="text-xs text-gray-500 space-y-1.5">
                      <li>• Comprensión profunda</li>
                      <li>• Análisis e inferencia</li>
                      <li>• Argumentación con evidencia</li>
                      <li>• Resolución de problemas</li>
                      <li>• Metacognición</li>
                    </ul>
                  </div>
                  <div className="bg-sky-50 border border-sky-100 rounded-2xl p-4">
                    <p className="text-xs font-semibold text-sky-700 mb-1">Sin alucinaciones</p>
                    <p className="text-[11px] text-sky-700/80 leading-relaxed">
                      Si Reto Avanzo no tiene un dato confirmado, te dirá &quot;no tengo información confirmada&quot;. No invents nada por encima.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/reto-avanzo/perfil")}
                    className="w-full gap-2 border-sky-200 text-sky-700 hover:bg-sky-50"
                    data-testid="ra-my-profile-btn"
                  >
                    <Sparkles className="w-4 h-4" />
                    Mi Perfil Cognitivo
                  </Button>
                </>
              ) : (
                <>
                  {/* Unit info */}
                  {unitName && (
                    <div className="bg-white rounded-2xl border border-gray-100 p-4">
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Unidad actual</p>
                      <p className="font-semibold text-gray-900 text-sm">{unitName}</p>
                    </div>
                  )}

                  {/* Bloom Thermometer */}
                  <BloomThermometer 
                    currentLevel={currentBloomLevel}
                    sessionPoints={sessionPoints}
                    totalPoints={totalPoints}
                    streak={streak}
                  />

                  {/* Tips */}
                  <div className="bg-white rounded-2xl border border-gray-100 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-purple-500" />
                      <p className="text-xs font-semibold text-gray-700">Tip</p>
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      Responde las preguntas de QHUB.BUDDY para avanzar en los niveles taxonómicos y ganar más puntos.
                    </p>
                  </div>

                  {/* Indicadores de la unidad — visible en todas las materias */}
                  {!config.retoAvanzo && (
                    <IndicatorProgressPanel
                      grade={user?.grade || "8"}
                      subject={subject}
                      unit={unitName}
                      refreshKey={indicatorsRefreshKey}
                    />
                  )}
                </>
              )}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {showSidebar && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            className="md:hidden fixed inset-y-0 right-0 w-72 bg-gray-50 border-l border-gray-200 z-50 overflow-y-auto"
          >
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">{config.retoAvanzo ? "Reto Avanzo" : "Tu progreso"}</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowSidebar(false)}
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>

              {config.retoAvanzo ? (
                <div className="bg-white rounded-2xl border border-gray-100 p-4">
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Entrenamiento cognitivo adaptativo. Aquí no estudias materias, entrenas cómo piensas.
                  </p>
                </div>
              ) : (
                <>
                  <BloomThermometer
                    currentLevel={currentBloomLevel}
                    sessionPoints={sessionPoints}
                    totalPoints={totalPoints}
                    streak={streak}
                  />
                  <IndicatorProgressPanel
                    grade={user?.grade || "8"}
                    subject={subject}
                    unit={unitName}
                    refreshKey={indicatorsRefreshKey}
                  />
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TutorChat;
